import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

// Route meta types are defined elsewhere or via per-file annotations.
// Avoid defining unused types in this file; keep router focused on routes only.

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'dashboard',
    component: () => import('../views/DashboardView.vue'),
    meta: {
      title: 'dashboard.title',
      subtitle: 'dashboard.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/units',
    name: 'units',
    component: () => import('../views/UnitRegisterView.vue'),
    meta: {
      title: 'pages.unitRegister.title',
      subtitle: 'pages.unitRegister.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/unit-reports',
    name: 'unit-reports',
    component: () => import('../views/UnitReportsView.vue'),
    meta: {
      title: 'pages.unitReports.title',
      subtitle: 'pages.unitReports.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/work-orders',
    name: 'work-orders',
    component: () => import('../views/WorkOrdersView.vue'),
    meta: {
      title: 'pages.workOrders.title',
      subtitle: 'pages.workOrders.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/breakdowns',
    name: 'breakdowns',
    component: () => import('../views/BreakdownsView.vue'),
    meta: {
      title: 'pages.breakdowns.title',
      subtitle: 'pages.breakdowns.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/inventory',
    name: 'inventory',
    component: () => import('../views/InventoryView.vue'),
    meta: {
      title: 'pages.inventory.title',
      subtitle: 'pages.inventory.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: { title: 'auth.loginTitle', subtitle: 'auth.loginSubtitle' },
  },
  // --- Rute Purchase Requests ---
  {
    path: '/purchase-requests',
    name: 'purchase-requests',
    component: () => import('../views/PurchaseRequestsView.vue'),
    meta: {
      title: 'pages.purchaseRequests.title',
      subtitle: 'pages.purchaseRequests.subtitle',
      requiresAuth: true,
    },
  },
  // --- Rute Baru (Penyempurnaan 2) ---
  {
    path: '/maintenance-requests',
    name: 'maintenance-requests',
    component: () => import('../views/MaintenanceRequestsView.vue'),
    meta: {
      title: 'pages.maintenanceRequests.title',
      subtitle: 'pages.maintenanceRequests.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/fuel-management',
    name: 'fuel-management',
    component: () => import('../views/FuelManagementView.vue'),
    meta: {
      title: 'pages.fuelManagement.title',
      subtitle: 'pages.fuelManagement.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/sites',
    name: 'sites',
    component: () => import('../views/SiteView.vue'),
    meta: {
      title: 'pages.siteManagement.title',
      subtitle: 'pages.siteManagement.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/pm-standard-parts',
    name: 'pm-standard-parts',
    component: () => import('../views/PmStandardPartsView.vue'),
    meta: {
      title: 'pages.pmStandardParts.title',
      subtitle: 'pages.pmStandardParts.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/pm-monitoring',
    name: 'pm-monitoring',
    component: () => import('../views/PmMonitoringView.vue'),
    meta: {
      title: 'pages.pmMonitoring.title',
      subtitle: 'pages.pmMonitoring.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/maintenance-backlog',
    name: 'maintenance-backlog',
    component: () => import('../views/MaintenanceBacklogView.vue'),
    meta: {
      title: 'backlog.title',
      subtitle: 'backlog.subtitle',
      requiresAuth: true,
    },
  },
  // --- Rute Fleet Management ---
  {
    path: '/fleet',
    component: () => import('../views/FleetView.vue'),
    redirect: '/fleet/database',
    meta: { requiresAuth: true },
    children: [
      {
        path: 'database',
        name: 'fleet-database',
        component: () => import('../views/UnitDatabaseView.vue'),
        meta: {
          title: 'pages.unitDatabase.title',
          subtitle: 'pages.unitDatabase.subtitle',
        },
      },
      {
        path: 'population',
        name: 'fleet-population',
        meta: {
          title: 'pages.unitPopulation.title',
          subtitle: 'pages.unitPopulation.subtitle',
        },
        redirect: '/fleet/database',
      },
      {
        path: 'schedule',
        name: 'fleet-schedule',
        component: () => import('../views/ServiceScheduleView.vue'),
        meta: {
          title: 'pages.serviceSchedule.title',
          subtitle: 'pages.serviceSchedule.subtitle',
        },
      },
      {
        path: 'performance',
        name: 'fleet-performance',
        meta: {
          title: 'pages.unitPerformance.title',
          subtitle: 'pages.unitPerformance.subtitle',
        },
        redirect: '/fleet/database',
      },
    ],
  },
  {
    path: '/user-permissions',
    name: 'user-permissions',
    component: () => import('../views/UserPermissionsView.vue'),
    meta: {
      title: 'pages.userPermissions.title',
      subtitle: 'pages.userPermissions.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('../views/ProfileView.vue'),
    meta: {
      title: 'profile.title',
      subtitle: 'profile.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('../views/SettingsView.vue'),
    meta: {
      title: 'settings.title',
      subtitle: 'settings.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/help-and-flow',
    name: 'help-and-flow',
    component: () => import('../views/HelpAndFlowView.vue'),
    meta: {
      title: 'pages.helpAndFlow.title',
      subtitle: 'pages.helpAndFlow.subtitle',
    },
  },
  {
    path: '/audit-trail',
    name: 'audit-trail',
    component: () => import('../views/AuditTrailView.vue'),
    meta: {
      title: 'pages.auditTrail.title',
      subtitle: 'pages.auditTrail.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/executive-summary',
    name: 'executive-summary',
    component: () => import('../views/ExecutiveSummaryView.vue'),
    meta: {
      title: 'sidebar.menu.executiveSummary',
      subtitle: 'dashboard.subtitle',
      requiresAuth: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/NotFoundView.vue'),
    meta: {
      title: 'common.notFound',
      subtitle: 'common.pageNotFound',
    },
  },
]

const router = createRouter({
  history: createWebHistory((import.meta as any).env?.BASE_URL ?? '/'),
  routes,
})

// NOTE: route auth guard is handled centrally in `main.ts` after auth is initialized.
// Keeping guards here can cause duplicate/conflicting behavior when auth state
// is resolved asynchronously (e.g. Sanctum session revalidation).

export default router
